import React from 'react';
import PropTypes from 'prop-types';
import {Provider} from 'react-redux';
import {createStore, combineReducers, compose} from 'redux';
import ConnectedIntlProvider from './connected-intl-provider.jsx';

import localesReducer, {initLocale, localesInitialState} from '../reducers/locales';

import {setPlayer, setFullScreen} from '../reducers/mode.js';

import locales from './mstem-supported-locales'; // MSTEM-MODIFIED: vi included
import {detectLocale} from './detect-locale';
import {applyTheme, DEFAULT_THEME, VALID_THEMES} from './mstem-theme';
import {readTheme} from './mstem-storage';
import {
    isSeeded,
    markSeeded,
    readMyProjects,
    writeMyProjects
} from './mstem-project-storage';
import MOCK_PROJECTS from './mstem-mock/projects';

// MSTEM-ADDED Phase 2 GEN-02: synchronously bootstrap theme from
// localStorage BEFORE React renders, so users do not see a "flash of
// wrong theme" on reload. Mirrors the Phase 1 locale-bootstrap pattern.
// Safe in unsupported-browser path: applyTheme is a no-op if document is
// undefined.
const __mstemBootTheme = (function () {
    const stored = readTheme();
    if (stored !== null && VALID_THEMES.indexOf(stored) !== -1) {
        return stored;
    }
    return DEFAULT_THEME;
}());
applyTheme(__mstemBootTheme);

// MSTEM-ADDED Phase 3 PRJ-01 (+ Phase 5 / Plan 05-01 SMH-03, Assumption A2):
// first-boot seed of the mock MBF projects into mstem.myProjects[] localStorage.
// Runs ONCE per seed version (tracked via seedHash). The version was bumped
// v1 → v2 in Phase 5 (mstem-project-storage.isSeeded/markSeeded) so users who
// already booted Phase 3 re-run the seed once and receive the 3 new SmartHome
// scenarios.
//
// MERGE (not overwrite): we union any MOCK_PROJECTS entry whose `id` is not
// already in the stored list onto the user's existing projects. This ADDS the
// new scenarios for v1 users WITHOUT clobbering their saved (non-mock) projects,
// and stays idempotent per version (re-running with all ids present is a no-op).
// Then hydrate the reducer slice from localStorage so OpenProjectModal renders
// the merged list. Wrapped in try/catch for private-browsing / SSR safety.
const __mstemBootMyProjects = (function () {
    try {
        if (!isSeeded()) {
            const existing = readMyProjects();
            const existingList = Array.isArray(existing) ? existing : [];
            const existingIds = existingList.map(entry => entry && entry.id);
            const additions = MOCK_PROJECTS.filter(mock => existingIds.indexOf(mock.id) === -1);
            const merged = existingList.concat(additions);
            writeMyProjects(merged);
            markSeeded();
        }
        const stored = readMyProjects();
        return Array.isArray(stored) ? stored : MOCK_PROJECTS.slice();
    } catch (e) {
        return MOCK_PROJECTS.slice();
    }
}());

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

/*
 * Higher Order Component to provide redux state. If an `intl` prop is provided
 * it will override the internal `intl` redux state
 * @param {React.Component} WrappedComponent - component to provide state for
 * @param {boolean} localesOnly - only provide the locale state, not everything
 *                      required by the GUI. Used to exclude excess state when
                        only rendering modals, not the GUI.
 * @returns {React.Component} component with redux and intl state provided
 */
const AppStateHOC = function (WrappedComponent, localesOnly) {
    class AppStateWrapper extends React.Component {
        constructor (props) {
            super(props);
            let initialState = {};
            let reducers = {};
            let enhancer;

            let initializedLocales = localesInitialState;
            const locale = detectLocale(Object.keys(locales));
            if (locale !== 'en') {
                initializedLocales = initLocale(initializedLocales, locale);
            }
            if (localesOnly) {
                // Used for instantiating minimal state for the unsupported
                // browser modal
                reducers = {locales: localesReducer};
                initialState = {locales: initializedLocales};
                enhancer = composeEnhancers();
            } else {
                // You are right, this is gross. But it's necessary to avoid
                // importing unneeded code that will crash unsupported browsers.
                const guiRedux = require('../reducers/gui');
                const guiReducer = guiRedux.default;
                const {
                    guiInitialState,
                    guiMiddleware,
                    initFullScreen,
                    initPlayer,
                    initTelemetryModal
                } = guiRedux;
                const {ScratchPaintReducer} = require('scratch-paint');

                let initializedGui = guiInitialState;
                // MSTEM-ADDED Phase 2 GEN-02: seed theme slice with the
                // value already applied to <html> at module load above.
                initializedGui = Object.assign({}, initializedGui, {
                    theme: {currentTheme: __mstemBootTheme}
                });
                // MSTEM-ADDED Phase 3 PRJ-01: hydrate myProjects slice from
                // localStorage (seeded with 5 MBF mock projects on first boot —
                // see __mstemBootMyProjects above). currentId stays null
                // because no project is "active" at boot — modals dispatch
                // setCurrentProject when user creates/loads a project.
                initializedGui = Object.assign({}, initializedGui, {
                    myProjects: {
                        list: __mstemBootMyProjects.slice(),
                        currentId: null
                    }
                });
                if (props.isFullScreen || props.isPlayerOnly) {
                    if (props.isFullScreen) {
                        initializedGui = initFullScreen(initializedGui);
                    }
                    if (props.isPlayerOnly) {
                        initializedGui = initPlayer(initializedGui);
                    }
                } else if (props.showTelemetryModal) {
                    initializedGui = initTelemetryModal(initializedGui);
                }
                reducers = {
                    locales: localesReducer,
                    scratchGui: guiReducer,
                    scratchPaint: ScratchPaintReducer
                };
                initialState = {
                    locales: initializedLocales,
                    scratchGui: initializedGui
                };
                enhancer = composeEnhancers(guiMiddleware);
            }
            const reducer = combineReducers(reducers);
            this.store = createStore(
                reducer,
                initialState,
                enhancer
            );
        }
        componentDidUpdate (prevProps) {
            if (localesOnly) return;
            if (prevProps.isPlayerOnly !== this.props.isPlayerOnly) {
                this.store.dispatch(setPlayer(this.props.isPlayerOnly));
            }
            if (prevProps.isFullScreen !== this.props.isFullScreen) {
                this.store.dispatch(setFullScreen(this.props.isFullScreen));
            }
        }
        render () {
            const {
                isFullScreen, // eslint-disable-line no-unused-vars
                isPlayerOnly, // eslint-disable-line no-unused-vars
                showTelemetryModal, // eslint-disable-line no-unused-vars
                ...componentProps
            } = this.props;
            return (
                <Provider store={this.store}>
                    <ConnectedIntlProvider>
                        <WrappedComponent
                            {...componentProps}
                        />
                    </ConnectedIntlProvider>
                </Provider>
            );
        }
    }
    AppStateWrapper.propTypes = {
        isFullScreen: PropTypes.bool,
        isPlayerOnly: PropTypes.bool,
        isTelemetryEnabled: PropTypes.bool,
        showTelemetryModal: PropTypes.bool
    };
    return AppStateWrapper;
};

export default AppStateHOC;
